import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const PreludeInCMajorViewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Prelude in C Major
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV846/wtk1-prelude1/wtk1-prelude1-a4.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Prelude in C Major.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-6144">
      <Navbar data-id="element-6145" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-6146">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-6147">
          <img src="https://cdn.magicpatterns.com/uploads/7oUwphpLRiC5PZuGw98eyU/IMG_4447.jpg" alt="Prelude in C Major" className="w-full h-64 md:h-80 object-cover" data-id="element-6148" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-6149" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-6150">
            <div className="max-w-7xl mx-auto" data-id="element-6151">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-6152">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-6153" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-6154">
                Prelude in C Major
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-6155">
                BWV 846, from The Well-Tempered Clavier, Book I
              </p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-6156">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-6157">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-6158">
          <div className="flex items-center space-x-2" data-id="element-6159">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-6160">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-6161" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6162">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-6163">
                  <ZoomInIcon className="h-5 w-5" data-id="element-6164" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-6165">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-6166">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-6167" />
              <span className="hidden sm:inline" data-id="element-6168">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-6169">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-6170" />
              <span className="hidden sm:inline" data-id="element-6171">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-6172">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-6173">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-6174">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Prelude in C Major Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-6175" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-6176">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6177">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-6178">
            <div data-id="element-6179">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6180">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-6181">
                <div data-id="element-6182">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6183">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6184">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-6185">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6186">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6187">
                    1722
                  </dd>
                </div>
                <div data-id="element-6188">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6189">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6190">
                    C Major
                  </dd>
                </div>
                <div data-id="element-6191">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6192">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6193">
                    BWV 846
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-6194">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-6195">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6196">
                The Prelude in C Major is the opening piece of Bach's
                Well-Tempered Clavier, Book I. This iconic work features a
                flowing, arpeggiated pattern that creates a serene and
                meditative atmosphere. The piece is often used as a pedagogical
                tool for developing even touch and smooth legato playing. Its
                simple yet elegant structure has made it one of the most
                recognizable pieces in the classical piano repertoire.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-6197" />
    </div>;
};